import { CollideEvent, CollideId, isOneHitCollider } from '../../defs/collide';
import type { StatueBlockEntity } from '../../entities/BlockEntity';
import { despawnBlockEntity } from '../../entities/BlockEntity';
import { GameScene } from '../../GameScene';
import { BreakEffect } from '../effects/BreakEffect';
import { InvalidEffect } from '../effects/InvalidEffect';
import { StatueBlockEffect } from '../effects/StatueBlockEffect';
import { CollideHandler, ICollision } from './ICollision';

// fields
//-----------------------------------------------------------------------------
//const sounds = ['puzzle-lock-destroy', 'puzzle-lock-poke'];

/*
    statue block collision resolver
*/
export class StatueCollision implements ICollision {
  // fields
  //-------------------------------------------------------------------------
  // input
  private readonly _scene: GameScene;
  private readonly _entity: StatueBlockEntity;
  // map
  private readonly _resolvers: { [key in CollideId]?: CollideHandler } = {
    tap: this._resolveTap,
    block: this._resolveBlock,
    attack: this._resolveAttack,
  };

  // init
  //-------------------------------------------------------------------------
  constructor(scene: GameScene, entity: StatueBlockEntity) {
    this._scene = scene;
    this._entity = entity;
  }

  // impl
  //-------------------------------------------------------------------------
  public async resolve(event: CollideEvent): Promise<boolean> {
    return this._resolvers[event.id]?.call(this, event) ?? false;
  }

  // private: resolvers
  //-------------------------------------------------------------------------
  private async _resolveTap(event: CollideEvent) {
    // invalid effect
    await new InvalidEffect(this._entity).execute();
    return false;
  }

  private async _resolveBlock(event: CollideEvent) {
    const collider = event.collider;

    // attack if basic block collider
    if (
      collider?.type === 'block' &&
      collider.entity.c.block.props.type === 'basic'
    ) {
      this._attack();
      return true;
    }
    return false;
  }

  private async _resolveAttack(event: CollideEvent) {
    if (isOneHitCollider(event.collider?.type)) {
      this._entity.c.blockStatue.count = 0;
    }
    this._attack();
    return true;
  }

  // private: actions
  //-------------------------------------------------------------------------
  private async _attack() {
    const entity = this._entity;
    const position = entity.c.position.mapPosition;
    const component = entity.c.blockStatue;

    const effect = new StatueBlockEffect(this._scene, { entity });

    if (component.count === 2) {
      effect.animateChipBreak();
    } else {
      effect.animateCrumbles();
    }

    // if count is 0
    if (component.count <= 0) {
      effect.animateBlast();
      despawnBlockEntity(this._scene, this._entity);

      // else decrement count
    } else {
      --component.count;
      this._scene?.events.publish({ id: 'poke' });
    }
  }
}
